import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ChatMessageItem from './ChatMessageItem';
import ChatLoadingIndicator from './ChatLoadingIndicator';
import { Message } from '../hooks/useChatMessages';

interface ChatMessageListProps {
    messages: Message[];
    user: any;
    isTyping: boolean;
    loadingProgress: string;
    loadingTime: number;
    isGeneratingRoadmap: boolean;
    showScrollToBottom: boolean;
    onScrollToBottom: () => void;
    editingMessage: string | null;
    newMessage: string;
    setNewMessage: (message: string) => void;
    onConfirmEdit: (messageId: string, newContent: string) => void;
    setEditingMessage: (messageId: string | null) => void;
    onRoadmapTrigger: (module: any) => void;
}

const ChatMessageList: React.FC<ChatMessageListProps> = ({
    messages,
    user,
    isTyping,
    loadingProgress,
    loadingTime,
    isGeneratingRoadmap,
    showScrollToBottom,
    onScrollToBottom,
    editingMessage,
    newMessage,
    setNewMessage,
    onConfirmEdit,
    setEditingMessage,
    onRoadmapTrigger
}) => {
    const { t } = useTranslation();

    return (
        <Box
            data-messages-container
            sx={{
                flexGrow: 1,
                overflow: 'auto',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                position: 'relative'
            }}
        >
            {messages.length === 0 ? (
                <Box sx={{
                    textAlign: 'center',
                    mt: 4,
                    p: 4
                }}>
                    <img
                        src="/logo192.png"
                        alt="PetSnap Assistant"
                        style={{
                            width: '64px',
                            height: '64px',
                            objectFit: 'contain',
                            marginBottom: '16px'
                        }}
                    />
                    <Typography variant="h6" gutterBottom>
                        {t('helloMessage')}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {t('helloDescription')}
                    </Typography>
                </Box>
            ) : (
                messages.map((message) => (
                    <ChatMessageItem
                        key={message.id}
                        message={message}
                        user={user}
                        editingMessage={editingMessage}
                        newMessage={newMessage}
                        setNewMessage={setNewMessage}
                        onConfirmEdit={onConfirmEdit}
                        setEditingMessage={setEditingMessage}
                        onRoadmapTrigger={onRoadmapTrigger}
                    />
                ))
            )}

            {/* Typing Indicator */}
            {isTyping && (
                <ChatLoadingIndicator
                    type="typing"
                    loadingProgress={loadingProgress}
                    loadingTime={loadingTime}
                    messages={messages}
                />
            )}

            {/* Roadmap Generation Indicator */}
            {isGeneratingRoadmap && (
                <ChatLoadingIndicator
                    type="roadmap"
                    messages={messages}
                />
            )}

            {/* Scroll to Bottom Button */}
            {showScrollToBottom && (
                <Box sx={{
                    position: 'absolute',
                    bottom: 20,
                    right: 20,
                    zIndex: 1000
                }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onScrollToBottom}
                        sx={{
                            minWidth: 48,
                            height: 48,
                            borderRadius: '50%',
                            boxShadow: 3,
                            '&:hover': {
                                transform: 'scale(1.1)',
                                transition: 'transform 0.2s ease-in-out'
                            }
                        }}
                    >
                        ↓
                    </Button>
                </Box>
            )}
        </Box>
    );
};

export default ChatMessageList;
